import DropDown, { ContextMenuItem } from "@/src/components/ui-kit/drop-down";
import { useOrganizationsQuery } from "@/src/services/uam/permissions.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import { ArrowDown2 } from "iconsax-react-nativejs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";

const DEFAULT_ORG_ID = "default";

type PreferredOrgPickerProps = Readonly<{
  value: string | null | undefined;
  canEdit: boolean;
  membershipOrgIds: readonly string[];
  onChange: (orgId: string | null) => void;
}>;

const PreferredOrgPicker = ({
  value,
  canEdit,
  membershipOrgIds,
  onChange,
}: PreferredOrgPickerProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { data: allOrgs = [] } = useOrganizationsQuery();

  const realOrgIds = useMemo(
    () => membershipOrgIds.filter((id) => id !== DEFAULT_ORG_ID),
    [membershipOrgIds],
  );

  const availableOrgs = useMemo(
    () => allOrgs.filter((org) => realOrgIds.includes(org.itemId)),
    [allOrgs, realOrgIds],
  );

  const selectedOrgName = useMemo(
    () => availableOrgs.find((org) => org.itemId === value)?.name ?? null,
    [availableOrgs, value],
  );

  const items: ContextMenuItem[] = useMemo(
    () => [
      {
        label: t("LABEL_NO_PREFERENCE"),
        onPress: () => onChange(null),
      },
      ...availableOrgs.map((org) => ({
        label: org.name ?? org.itemId,
        onPress: () => onChange(org.itemId),
      })),
    ],
    [availableOrgs, onChange, t],
  );

  return (
    <View className="flex-1 gap-1">
      <Text className="text-base text-onyx">{t("LABEL_PREFERRED_ORG")}</Text>
      <View className="flex-row items-center md:border md:border-ash-3 bg-ash rounded-[10px] px-3 h-[50px] gap-2">
        {canEdit ? (
          <DropDown
            items={items}
            menuWidth={200}
            trigger={
              <View className="flex-row items-center gap-2">
                <Text className="text-slate text-base">
                  {selectedOrgName ?? t("LABEL_NO_PREFERENCE")}
                </Text>
                <ArrowDown2
                  size={18}
                  color={themeVars["--color-ash-2"] as string}
                />
              </View>
            }
          />
        ) : (
          <Text className="text-slate text-base flex-1">
            {selectedOrgName ?? t("LABEL_NO_PREFERENCE")}
          </Text>
        )}
      </View>
    </View>
  );
};

export default PreferredOrgPicker;
